import React, { useCallback, useEffect, useRef, useState } from "react";

const MAX_LENGTH = 800; // mirrors the backend limit (AI_LIMITS.MAX_QUERY_LENGTH)

const MicIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <rect x="9" y="2.5" width="6" height="11" rx="3" stroke="currentColor" strokeWidth="1.8" />
    <path d="M5.5 11v.5a6.5 6.5 0 0 0 13 0V11M12 18v3.5M9 21.5h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const SendIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M12 19V5M5.5 11.5 12 5l6.5 6.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const getSpeechRecognition = () =>
  typeof window === "undefined" ? null : window.SpeechRecognition || window.webkitSpeechRecognition || null;

export default function Composer({ onSend, disabled, remaining, autoFocus, autoStartVoice }) {
  const [value, setValue] = useState("");
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState("");
  const areaRef = useRef(null);
  const recognitionRef = useRef(null);

  const canSpeak = !!getSpeechRecognition();
  const trimmed = value.trim();

  const resize = useCallback(() => {
    const el = areaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);

  useEffect(resize, [value, resize]);

  useEffect(() => {
    if (autoFocus) areaRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => () => recognitionRef.current?.stop?.(), []);

  const submit = () => {
    if (disabled || !trimmed) return;
    recognitionRef.current?.stop?.();
    if (onSend(trimmed) !== false) setValue("");
  };

  // Voice input is the browser's own speech recognition, carried over from the
  // previous AI search modal — it only fills the text box, so a spoken request
  // then goes through exactly the same path as a typed one.
  const toggleVoice = () => {
    if (listening) {
      recognitionRef.current?.stop?.();
      return;
    }

    const Recognition = getSpeechRecognition();
    if (!Recognition) return;

    setVoiceError("");
    const recognition = new Recognition();
    recognition.lang = "uk-UA";
    recognition.interimResults = true;
    recognition.continuous = false;

    const base = value.trim();

    recognition.onstart = () => setListening(true);
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map((r) => r[0]?.transcript || "")
        .join(" ")
        .trim();
      setValue([base, transcript].filter(Boolean).join(" ").slice(0, MAX_LENGTH));
    };
    recognition.onerror = () => {
      setListening(false);
      setVoiceError("Не вдалося розпізнати голос. Спробуйте ще раз або напишіть текстом.");
    };
    recognition.onend = () => {
      setListening(false);
      areaRef.current?.focus();
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  // entryMode "voice" (SearchHero's mic-menu option, see CvantoAgentModal) —
  // start listening the moment this composer mounts, same as the previous
  // AI search modal did when opened via the mic.
  useEffect(() => {
    if (autoStartVoice && canSpeak) toggleVoice();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="cvai-composer">
      {voiceError && (
        <div className="cvai-composer__hint cvai-composer__hint--error" role="alert">
          {voiceError}
        </div>
      )}

      <div className={`cvai-composer__box ${listening ? "is-listening" : ""}`}>
        <textarea
          ref={areaRef}
          className="cvai-composer__input"
          rows={1}
          value={value}
          maxLength={MAX_LENGTH}
          placeholder={listening ? "Слухаю…" : "Напишіть, що шукаєте…"}
          aria-label="Повідомлення для Cvanto AI"
          enterKeyHint="send"
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              submit();
            }
          }}
        />

        {canSpeak && (
          <button
            type="button"
            className={`cvai-composer__icon-btn ${listening ? "is-active" : ""}`}
            onClick={toggleVoice}
            aria-label={listening ? "Зупинити запис" : "Сказати голосом"}
            aria-pressed={listening}
            disabled={disabled}
          >
            <MicIcon />
          </button>
        )}

        <button
          type="button"
          className="cvai-composer__send"
          onClick={submit}
          disabled={disabled || !trimmed}
          aria-label="Надіслати"
        >
          <SendIcon />
        </button>
      </div>

      {typeof remaining === "number" && remaining <= 5 && (
        <div className="cvai-composer__hint">
          {remaining > 0 ? `Залишилось повідомлень цього місяця: ${remaining}` : "Ліміт повідомлень на цей місяць вичерпано"}
        </div>
      )}
    </div>
  );
}
